import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import './OptionScope.css';

function fmtIdx(n) {
  return Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPnl(n) {
  const v = Number(n) || 0;
  const sign = v > 0 ? '+' : '';
  return `${sign}₹${Math.abs(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function fmtStrike(n) {
  return Number(n || 0).toLocaleString('en-IN');
}

function IndexTicker({ card, active, onSelect, arming }) {
  if (!card) return null;
  const short = (card.label || card.id || '')
    .replace(/^NIFTY\s+/i, '')
    .replace(/\s+NIFTY$/i, '')
    .replace('INDIA ', '');
  return (
    <button
      type="button"
      className={`os-ticker ${active ? 'active' : ''} ${card.up ? 'up' : 'down'} ${!card.tradeable ? 'watch' : ''}`}
      onClick={() => onSelect?.(card)}
      disabled={arming && active}
      title={card.tradeable ? `Chart + auto-trade ${card.id}` : `${card.id} chart only`}
    >
      <span className="os-ticker-name">{short || card.id}</span>
      <span className="os-ticker-price">{fmtIdx(card.price)}</span>
      <span className="os-ticker-chg">
        {card.changePts >= 0 ? '+' : ''}
        {fmtIdx(card.changePts)}
        {' '}
        ({card.changePct >= 0 ? '+' : ''}
        {fmtIdx(card.changePct)}%)
      </span>
      <em className="os-ticker-tag">
        {card.tradeable ? `1 LOT × ${card.lotSize || '—'}` : 'CHART'}
      </em>
    </button>
  );
}

function TradeInstructionBar({ instruction }) {
  if (!instruction) return null;
  const tone = String(instruction.action || 'WAIT').toLowerCase();
  return (
    <section className={`os-trade-instruction ${tone}`}>
      <div className="os-trade-instruction-head">
        <div>
          <span className="os-trade-action">{instruction.action}</span>
          <h2>{instruction.headline}</h2>
          <p>{instruction.reason}</p>
        </div>
        <div className="os-trade-verdict">
          <strong>{instruction.readyToTrade ? 'ORDER READY' : instruction.action === 'HOLD' ? 'MANAGE OPEN TRADE' : 'NO TRADE'}</strong>
          <span>{instruction.moneyness} · {instruction.product} · {instruction.lots} LOT</span>
        </div>
      </div>

      {instruction.strategyKind === 'BEAR_CALL_SPREAD' ? (
        <div className="os-trade-instruction-grid">
          <div><span>First · Hedge BUY</span><strong>{instruction.hedgeLeg?.strike} CE @ ~₹{fmtIdx(instruction.hedgeLeg?.entryPremium)}</strong></div>
          <div><span>Second · CALL SELL</span><strong>{instruction.shortLeg?.strike} CE @ ~₹{fmtIdx(instruction.shortLeg?.entryPremium)}</strong></div>
          <div><span>Expiry</span><strong>{instruction.expiryLabel || 'Nearest expiry'}</strong></div>
          <div><span>Quantity</span><strong>{instruction.units} units each · 1 lot</strong></div>
          <div><span>Net credit</span><strong>₹{Number(instruction.netCredit || 0).toLocaleString('en-IN')}</strong></div>
          <div><span>Blocked risk margin</span><strong>₹{Number(instruction.marginBlocked || 0).toLocaleString('en-IN')}</strong></div>
          <div><span>Maximum profit NET</span><strong>₹{Number(instruction.maxProfitNet || 0).toLocaleString('en-IN')}</strong></div>
          <div><span>Maximum loss capped</span><strong>₹{Number(instruction.maxLossNet || 0).toLocaleString('en-IN')}</strong></div>
          <div><span>Bot profit target</span><strong>₹{Number(instruction.targetProfitRs || 0).toLocaleString('en-IN')}</strong></div>
          <div><span>Bot stop</span><strong>₹{Number(instruction.stopLossRs || 0).toLocaleString('en-IN')}</strong></div>
        </div>
      ) : (
        <div className="os-trade-instruction-grid">
          <div><span>Contract</span><strong>{instruction.index} {instruction.strike} {instruction.optionType}</strong></div>
          <div><span>Expiry</span><strong>{instruction.expiryLabel || 'Nearest expiry'}</strong></div>
          <div><span>Buy premium</span><strong>~₹{fmtIdx(instruction.modelPremium)} MODEL</strong></div>
          <div><span>Quantity</span><strong>{instruction.units} units · 1 lot</strong></div>
          <div><span>Premium outlay</span><strong>₹{Number(instruction.premiumCost || 0).toLocaleString('en-IN')}</strong></div>
          <div><span>Cash + buy charges</span><strong>₹{Number(instruction.estimatedCashRequired || 0).toLocaleString('en-IN')}</strong></div>
          <div><span>Stop loss</span><strong>₹{Number(instruction.stopLossRs || 0)} · premium ~₹{fmtIdx(instruction.stopPremium)}</strong></div>
          <div><span>Target</span><strong>₹{Number(instruction.targetProfitRs || 0)} · premium ~₹{fmtIdx(instruction.targetPremium)}</strong></div>
        </div>
      )}

      <p className="os-trade-route">
        INDEX MAP · Entry {fmtIdx(instruction.entrySpot)}
        {' → '}Bank {fmtIdx(instruction.bankSpot)}
        {' → '}Target {fmtIdx(instruction.targetSpot)}
        {' · '}Stop {fmtIdx(instruction.stopSpot)}
      </p>

      <div className="os-zerodha-guide">
        <strong>Zerodha में कैसे करें</strong>
        <ol>
          {(instruction.zerodhaSteps || []).map((step) => <li key={step}>{step}</li>)}
        </ol>
      </div>
      <p className="os-premium-warning">{instruction.premiumWarning}</p>
    </section>
  );
}

function ProfitBoard({ profit }) {
  if (!profit) {
    return (
      <section className="os-profit-board">
        <div className="os-profit-empty">Loading profit board…</div>
      </section>
    );
  }
  const dayNet = Number(profit.dayNet) || 0;
  const realized = Number(profit.realizedPnl) || 0;
  const unrealized = Number(profit.unrealizedPnl) || 0;
  const target = Number(profit.dayTarget) || 10000;
  const pct = Math.min(100, Math.max(0, Number(profit.progressPct) || 0));
  const open = profit.open;
  const scenario = profit.scenario;
  const goal = profit.goalPace;
  const tone = dayNet > 0.004 ? 'earn' : dayNet < -0.004 ? 'loss' : 'flat';

  return (
    <section className={`os-profit-board ${tone}`}>
      <header className="os-profit-head">
        <div>
          <h2>Today&apos;s profit · Real-world</h2>
          <p>{profit.state || '—'}</p>
          {scenario?.name ? (
            <p className="os-scenario-name">
              Scenario: <strong>{scenario.name}</strong>
              {scenario.quality != null ? ` · quality ${scenario.quality}` : ''}
              {scenario.allowEntry ? ' · READY' : ' · WAIT'}
            </p>
          ) : null}
        </div>
        <div className={`os-profit-hero ${tone}`}>
          <span className="os-profit-hero-label">Day net</span>
          <strong>{fmtPnl(dayNet)}</strong>
        </div>
      </header>

      <div className="os-profit-metrics">
        <div>
          <span>Booked (closed)</span>
          <strong className={realized >= 0 ? 'up' : 'down'}>{fmtPnl(realized)}</strong>
        </div>
        <div>
          <span>Open trade</span>
          <strong className={unrealized >= 0 ? 'up' : 'down'}>{fmtPnl(unrealized)}</strong>
        </div>
        <div>
          <span>Bank from</span>
          <strong>₹{Number(profit.bankProfitRs || 0).toLocaleString('en-IN')}</strong>
        </div>
        <div>
          <span>Trade target</span>
          <strong>₹{Number(profit.takeProfitRs || 0).toLocaleString('en-IN')}</strong>
        </div>
      </div>

      {goal && (
        <div className={`os-goal-pace ${goal.pathAvailable ? 'available' : 'tight'}`}>
          <div><span>NET goal</span><strong>₹{Number(goal.dailyNetTarget).toLocaleString('en-IN')}</strong></div>
          <div><span>Earned net</span><strong>{fmtPnl(goal.netEarned)}</strong></div>
          <div><span>Still required</span><strong>₹{Number(goal.remainingNet).toLocaleString('en-IN')}</strong></div>
          <div><span>Clean targets needed</span><strong>{goal.cleanTargetsNeeded} × ₹{Number(goal.perTradeNetTarget).toLocaleString('en-IN')}</strong></div>
          <div><span>Trades remaining</span><strong>{goal.tradesLeft} / {goal.maxTrades}</strong></div>
          <div><span>Daily loss cap</span><strong>−₹{Number(goal.maxDailyLoss).toLocaleString('en-IN')}</strong></div>
          <p>{goal.note}</p>
        </div>
      )}

      <div className="os-profit-progress" aria-label="Day goal progress">
        <div className="os-profit-progress-bar" style={{ width: `${pct}%` }} />
        <em>{pct.toFixed(0)}% of day goal ₹{target.toLocaleString('en-IN')}</em>
      </div>

      {open ? (
        <div className={`os-open-trade ${open.unrealized >= 0 ? 'up' : 'down'}`}>
          <div className="os-open-trade-top">
            <strong>
              {open.strategyKind === 'BEAR_CALL_SPREAD'
                ? `Open 1 lot HEDGED SHORT CALL ${open.shortLeg?.strike}/${open.hedgeLeg?.strike}`
                : `Open 1 lot ${open.side} ${fmtStrike(open.strike)}`}
            </strong>
            <span className={open.unrealized >= 0 ? 'up' : 'down'}>{fmtPnl(open.unrealized)}</span>
          </div>
          <div className="os-open-trade-grid">
            {open.strategyKind === 'BEAR_CALL_SPREAD' ? (
              <>
                <span>Short leg <b>SELL {open.shortLeg?.strike} CE</b></span>
                <span>Hedge leg <b>BUY {open.hedgeLeg?.strike} CE</b></span>
                <span>Risk margin <b>₹{Number(open.marginBlocked || 0).toLocaleString('en-IN')}</b></span>
                <span>Max profit NET <b>₹{Number(open.maxProfitNet || 0).toLocaleString('en-IN')}</b></span>
                <span>Max loss capped <b>₹{Number(open.maxLossNet || 0).toLocaleString('en-IN')}</b></span>
              </>
            ) : (
              <>
                <span>Entry prem <b>{fmtIdx(open.entryPremium)}</b></span>
                <span>Live prem <b>{fmtIdx(open.livePremium)}</b></span>
              </>
            )}
            <span>1 lot units <b>{Number(open.units || 0)}</b></span>
            <span>{open.strategyKind === 'BEAR_CALL_SPREAD' ? 'Margin blocked' : 'Premium outlay'} <b>₹{Number(open.premiumCost || 0).toLocaleString('en-IN')}</b></span>
            <span>Buy charges est. <b>₹{Number(open.estimatedBuyCharges || 0).toFixed(2)}</b></span>
            <span>Cash required <b>₹{Number(open.estimatedCashRequired || 0).toLocaleString('en-IN')}</b></span>
            <span>Trade target <b>₹{Number(open.tradeTargetRs).toLocaleString('en-IN')}</b></span>
            <span>
              {open.hitTradeTarget
                ? 'In target zone'
                : `Need ${fmtPnl(open.remainingToTradeTarget)} more`}
            </span>
          </div>
          <p className="os-open-trade-plan">{open.strategy}</p>
          {open.targetSpot != null && (
            <p className="os-open-trade-map">
              Map: Entry {fmtIdx(open.entrySpot)}
              {' → '}
              Target {fmtIdx(open.targetSpot)}
              {' · '}
              Bank {fmtIdx(open.bankSpot)}
              {' · '}
              Stop {fmtIdx(open.stopSpot)}
            </p>
          )}
        </div>
      ) : (
        <p className="os-profit-wait">
          No open lot — bot waits for VWAP+EMA confluence, then BUY CALL only (1 lot).
        </p>
      )}

      {(profit.lines || []).length > 0 && (
        <ul className="os-profit-lines">
          {profit.lines.map((line) => (
            <li key={line.id}>
              <span className={`os-side-badge ${line.side === 'CE' ? 'ce' : 'pe'}`}>{line.side}</span>
              <span>{line.label} · {line.status}</span>
              <strong className={(line.netPnl ?? line.pnl) >= 0 ? 'up' : 'down'}>
                {fmtPnl(line.netPnl ?? line.pnl)}
              </strong>
            </li>
          ))}
        </ul>
      )}

      <p className="os-profit-foot">{profit.summary}</p>
    </section>
  );
}

function chartTimeLabel(raw) {
  const n = Number(raw);
  const date = new Date(Number.isFinite(n) ? (n < 1e12 ? n * 1000 : n) : raw);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

const DEFAULT_CHART_PREFS = {
  intervalMin: 5,
  chartType: 'candles',
  rangeBars: 48,
  showVwap: true,
  showTradeLevels: true,
  showKeyLevels: false,
  showEma9: false,
  showEma21: false,
  showSma20: false,
  showVolume: true,
  showGrid: true,
  showLastPrice: true,
  showCrosshair: true
};

function loadChartPrefs(storageKey = 'optionScopeChartPrefs', initialIntervalMin = 5) {
  try {
    const saved = JSON.parse(window.localStorage.getItem(storageKey) || '{}');
    return { ...DEFAULT_CHART_PREFS, intervalMin: initialIntervalMin, ...saved };
  } catch {
    return { ...DEFAULT_CHART_PREFS, intervalMin: initialIntervalMin };
  }
}

function aggregateCandles(rows, factor) {
  const size = Math.max(1, Number(factor) || 1);
  if (size === 1) return rows;
  const out = [];
  for (let i = 0; i < rows.length; i += size) {
    const group = rows.slice(i, i + size);
    if (!group.length) continue;
    const open = Number(group[0].open);
    const close = Number(group[group.length - 1].close);
    out.push({
      t: group[0].t,
      open,
      high: Math.max(...group.map((c) => Number(c.high))),
      low: Math.min(...group.map((c) => Number(c.low))),
      close,
      volume: group.reduce((sum, c) => sum + (Number(c.volume) || 0), 0),
      bull: close >= open
    });
  }
  return out;
}

function heikinAshiCandles(rows) {
  let previousOpen = null;
  let previousClose = null;
  return rows.map((c) => {
    const close = (Number(c.open) + Number(c.high) + Number(c.low) + Number(c.close)) / 4;
    const open = previousOpen == null
      ? (Number(c.open) + Number(c.close)) / 2
      : (previousOpen + previousClose) / 2;
    const row = {
      ...c,
      open,
      close,
      high: Math.max(Number(c.high), open, close),
      low: Math.min(Number(c.low), open, close),
      bull: close >= open
    };
    previousOpen = open;
    previousClose = close;
    return row;
  });
}

function movingAverage(rows, period, exponential = false) {
  const alpha = 2 / (period + 1);
  let ema = null;
  return rows.map((row, index) => {
    if (exponential) {
      ema = ema == null ? Number(row.close) : Number(row.close) * alpha + ema * (1 - alpha);
      return ema;
    }
    if (index + 1 < period) return null;
    const windowRows = rows.slice(index + 1 - period, index + 1);
    return windowRows.reduce((sum, item) => sum + Number(item.close), 0) / period;
  });
}

function ChartToolbar({ prefs, intervalOptions = [5, 10, 15, 30], onChange, onReset, onFullscreen }) {
  const toggle = (key) => onChange({ [key]: !prefs[key] });
  return (
    <div className="os-chart-toolbar" aria-label="Chart filters">
      <div className="os-chart-tool-group">
        <span>Interval</span>
        {intervalOptions.map((min) => (
          <button
            key={min}
            type="button"
            className={prefs.intervalMin === min ? 'active' : ''}
            onClick={() => onChange({ intervalMin: min })}
          >
            {min}m
          </button>
        ))}
      </div>
      <label className="os-chart-select">
        <span>Chart</span>
        <select value={prefs.chartType} onChange={(e) => onChange({ chartType: e.target.value })}>
          <option value="candles">Candles</option>
          <option value="heikin">Heikin-Ashi</option>
          <option value="line">Line</option>
          <option value="area">Area</option>
        </select>
      </label>
      <label className="os-chart-select">
        <span>Range</span>
        <select
          value={String(prefs.rangeBars)}
          onChange={(e) => onChange({ rangeBars: e.target.value === 'all' ? 'all' : Number(e.target.value) })}
        >
          <option value="24">24 bars</option>
          <option value="48">48 bars</option>
          <option value="all">Full day</option>
        </select>
      </label>
      <details className="os-chart-menu">
        <summary>Indicators</summary>
        <div>
          <label><input type="checkbox" checked={prefs.showEma9} onChange={() => toggle('showEma9')} /> EMA 9</label>
          <label><input type="checkbox" checked={prefs.showEma21} onChange={() => toggle('showEma21')} /> EMA 21</label>
          <label><input type="checkbox" checked={prefs.showSma20} onChange={() => toggle('showSma20')} /> SMA 20</label>
          <label><input type="checkbox" checked={prefs.showVwap} onChange={() => toggle('showVwap')} /> VWAP</label>
          <label><input type="checkbox" checked={prefs.showVolume} onChange={() => toggle('showVolume')} /> Volume</label>
        </div>
      </details>
      <details className="os-chart-menu">
        <summary>Display</summary>
        <div>
          <label><input type="checkbox" checked={prefs.showTradeLevels} onChange={() => toggle('showTradeLevels')} /> Entry/Bank/Target/Stop</label>
          <label><input type="checkbox" checked={prefs.showKeyLevels} onChange={() => toggle('showKeyLevels')} /> Support/Resistance/ATM</label>
          <label><input type="checkbox" checked={prefs.showGrid} onChange={() => toggle('showGrid')} /> Grid</label>
          <label><input type="checkbox" checked={prefs.showLastPrice} onChange={() => toggle('showLastPrice')} /> Last price</label>
          <label><input type="checkbox" checked={prefs.showCrosshair} onChange={() => toggle('showCrosshair')} /> Crosshair</label>
        </div>
      </details>
      <div className="os-chart-tool-actions">
        <button type="button" onClick={onFullscreen} title="Fullscreen chart">⛶</button>
        <button type="button" onClick={onReset}>Reset</button>
      </div>
    </div>
  );
}

export function CenterChart({
  chart,
  footerNote = 'Detailed raw Option Chain main screen पर hidden है · केवल trade-relevant levels दिख रहे हैं',
  intervalOptions = [5, 10, 15, 30],
  initialIntervalMin = 5,
  prefsStorageKey = 'optionScopeChartPrefs'
}) {
  const allRows = chart?.candles || [];
  const [prefs, setPrefs] = useState(() => loadChartPrefs(prefsStorageKey, initialIntervalMin));
  const [hoverIndex, setHoverIndex] = useState(null);
  const chartSectionRef = useRef(null);
  useEffect(() => {
    window.localStorage.setItem(prefsStorageKey, JSON.stringify(prefs));
  }, [prefs, prefsStorageKey]);
  const rawIntervalMin = Math.max(1, Number.parseInt(String(chart?.interval || initialIntervalMin), 10) || initialIntervalMin);
  const mergedRows = useMemo(
    () => aggregateCandles(allRows, Math.max(1, Math.round(Number(prefs.intervalMin) / rawIntervalMin))),
    [allRows, prefs.intervalMin, rawIntervalMin]
  );
  const rangedRows = prefs.rangeBars === 'all'
    ? mergedRows
    : mergedRows.slice(-Math.max(1, Number(prefs.rangeBars) || 48));
  const rows = prefs.chartType === 'heikin' ? heikinAshiCandles(rangedRows) : rangedRows;
  const w = 1100;
  const h = 450;
  const padL = 66;
  const padR = 88;
  const padT = 18;
  const denseMinuteAxis = Number(prefs.intervalMin) === 1;
  const padB = denseMinuteAxis ? 66 : 38;
  const plotRight = w - padR;
  const plotW = plotRight - padL;
  const plotH = h - padT - padB;
  const title = chart?.label || chart?.symbol || 'Index';
  const analysis = chart?.analysis;
  const plan = chart?.plan;
  const updatePrefs = (patch) => setPrefs((current) => ({ ...current, ...patch }));
  const resetPrefs = () => setPrefs({ ...DEFAULT_CHART_PREFS, intervalMin: initialIntervalMin });
  const openFullscreen = () => {
    const node = chartSectionRef.current;
    if (!node) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else node.requestFullscreen?.();
  };

  if (!rows.length) {
    return (
      <section className="os-center-chart" ref={chartSectionRef}>
        <ChartToolbar prefs={prefs} intervalOptions={intervalOptions} onChange={updatePrefs} onReset={resetPrefs} onFullscreen={openFullscreen} />
        <header className="os-chart-head">
          <div>
            <h2>{title}</h2>
            <p>Intraday {prefs.intervalMin}m · loading live feed</p>
          </div>
          <strong className="os-chart-spot">{fmtIdx(chart?.spot)}</strong>
        </header>
        <div className="os-chart-empty">Loading clear {prefs.intervalMin}-minute chart…</div>
      </section>
    );
  }

  const strategyLines = (chart.strategyLines || []).map((line) => ({
    key: `strategy-${line.key}`,
    label: line.label,
    value: Number(line.value),
    color: line.color || '#fbbf24',
    solid: line.key === 'entry'
  })).filter((line) => line.value > 0);
  const guideLines = [
    ...(prefs.showVwap
      ? [{ key: 'vwap', label: 'VWAP', value: Number(chart.vwap), color: '#60a5fa' }]
      : []),
    ...(prefs.showTradeLevels ? strategyLines : []),
    ...(prefs.showKeyLevels
      ? [
        { key: 'support', label: 'SUPPORT', value: Number(chart.support), color: '#34d399' },
        { key: 'resistance', label: 'RESISTANCE', value: Number(chart.resistance), color: '#f87171' },
        { key: 'atm', label: 'ATM', value: Number(chart.atm), color: '#fbbf24' },
        { key: 'max-pain', label: 'MAX PAIN', value: Number(chart.maxPain), color: '#a78bfa' }
      ]
      : [])
  ].filter((line) => line.value > 0);
  const ema9 = movingAverage(rows, 9, true);
  const ema21 = movingAverage(rows, 21, true);
  const sma20 = movingAverage(rows, 20, false);
  const indicatorSeries = [
    ...(prefs.showEma9 ? [{ key: 'ema9', label: 'EMA 9', color: '#f59e0b', values: ema9 }] : []),
    ...(prefs.showEma21 ? [{ key: 'ema21', label: 'EMA 21', color: '#c084fc', values: ema21 }] : []),
    ...(prefs.showSma20 ? [{ key: 'sma20', label: 'SMA 20', color: '#22d3ee', values: sma20 }] : [])
  ];
  const priceValues = rows.flatMap((c) => [Number(c.low), Number(c.high)]).filter(Number.isFinite);
  const candleMin = Math.min(...priceValues);
  const candleMax = Math.max(...priceValues);
  const candleRange = Math.max(1, candleMax - candleMin);
  const nearbyGuides = guideLines.filter((line) => (
    line.value >= candleMin - candleRange * 0.55
    && line.value <= candleMax + candleRange * 0.55
  ));
  const indicatorValues = indicatorSeries.flatMap((series) => series.values).filter(Number.isFinite);
  const scaleValues = [...priceValues, ...nearbyGuides.map((line) => line.value), ...indicatorValues];
  const scaleMin = Math.min(...scaleValues);
  const scaleMax = Math.max(...scaleValues);
  const rawRange = Math.max(1, scaleMax - scaleMin);
  const min = scaleMin - rawRange * 0.08;
  const max = scaleMax + rawRange * 0.08;
  const range = max - min;
  const y = (v) => padT + (1 - (Number(v) - min) / range) * plotH;
  const barW = plotW / rows.length;
  const last = rows[rows.length - 1];
  const spot = Number(chart.spot || last.close);
  const up = (chart.dayChangePts || 0) >= 0;
  const yTicks = 6;
  const tickVals = Array.from({ length: yTicks }, (_, i) => min + (range * i) / (yTicks - 1));
  const minuteTickStep = Math.max(1, Math.ceil(rows.length / 60));
  const xTickIndexes = denseMinuteAxis
    ? rows.map((_, index) => index).filter((index) => index % minuteTickStep === 0 || index === rows.length - 1)
    : [...new Set([0, Math.floor((rows.length - 1) / 3), Math.floor((rows.length - 1) * 2 / 3), rows.length - 1])];
  const cryptoChart = chart?.marketType === 'crypto';
  const biasClass = analysis?.bias === 'CE' || analysis?.bias === 'BUY'
    ? 'up'
    : analysis?.bias === 'PE' || analysis?.bias === 'SELL' ? 'down' : '';
  const biasLabel = analysis?.bias === 'neutral'
    ? 'NEUTRAL'
    : cryptoChart
      ? (analysis?.bias === 'BUY' ? 'PAPER BUY' : 'EXIT / WAIT')
      : analysis?.bias === 'CE' ? 'BUY CALL' : 'WAIT (CALL-only)';
  const keyLevels = [
    ['VWAP', chart.vwap],
    ['Support', chart.support],
    ['Resistance', chart.resistance],
    ['ATM', chart.atm],
    ['Max pain', chart.maxPain]
  ].filter(([, value]) => Number(value) > 0);
  const hovered = hoverIndex != null ? rows[hoverIndex] : null;
  const linePoints = rows.map((row, i) => (
    `${padL + i * barW + barW / 2},${y(row.close)}`
  )).join(' ');
  const areaPoints = `${padL + barW / 2},${h - padB} ${linePoints} ${padL + (rows.length - 0.5) * barW},${h - padB}`;
  const maxVolume = Math.max(1, ...rows.map((row) => Number(row.volume) || 0));
  const renderCandles = prefs.chartType === 'candles' || prefs.chartType === 'heikin';

  return (
    <section className="os-center-chart" ref={chartSectionRef}>
      <ChartToolbar prefs={prefs} intervalOptions={intervalOptions} onChange={updatePrefs} onReset={resetPrefs} onFullscreen={openFullscreen} />
      <header className="os-chart-head">
        <div>
          <span className="os-chart-kicker">LIVE PRICE ACTION</span>
          <h2>{title} · {prefs.intervalMin}-minute {prefs.chartType === 'heikin' ? 'Heikin-Ashi' : prefs.chartType}</h2>
          <p>
            {rows.length} bars shown
            {mergedRows.length > rows.length ? ` · ${mergedRows.length} aggregated bars available` : ''}
          </p>
          {hovered && (
            <div className="os-chart-ohlc">
              <span>{chartTimeLabel(hovered.t)}</span>
              <b>O {fmtIdx(hovered.open)}</b>
              <b>H {fmtIdx(hovered.high)}</b>
              <b>L {fmtIdx(hovered.low)}</b>
              <b>C {fmtIdx(hovered.close)}</b>
              <b>V {Number(hovered.volume || 0).toLocaleString('en-IN')}</b>
            </div>
          )}
        </div>
        <div className="os-chart-price">
          <strong className={last.bull ? 'up' : 'down'}>{fmtIdx(spot)}</strong>
          <span className={up ? 'up' : 'down'}>
            {up ? '+' : ''}{fmtIdx(chart.dayChangePts)} ({up ? '+' : ''}{fmtIdx(chart.dayChangePct)}%)
          </span>
          {analysis?.ok && (
            <span className={`os-bias-pill ${biasClass}`}>{biasLabel} · {analysis.confidence}%</span>
          )}
        </div>
      </header>

      <div className="os-chart-level-strip">
        <div className={`os-chart-signal ${biasClass || 'neutral'}`}>
          <span>Signal</span>
          <strong>{analysis?.ok ? biasLabel : 'ANALYSING'}</strong>
        </div>
        {keyLevels.map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{fmtIdx(value)}</strong>
          </div>
        ))}
      </div>

      <div className="os-chart-legend" aria-label="Chart guide lines">
        {nearbyGuides.map((line) => (
          <span key={line.key}>
            <i style={{ background: line.color }} />
            {line.label} <strong>{fmtIdx(line.value)}</strong>
          </span>
        ))}
        {indicatorSeries.map((series) => (
          <span key={series.key}>
            <i style={{ background: series.color }} />
            {series.label}
          </span>
        ))}
        <em>Green candle = price up · Red candle = price down</em>
      </div>

      <div className="os-chart-canvas">
        <svg viewBox={`0 0 ${w} ${h}`} className="os-big-chart-svg" role="img" aria-label={`${title} clear intraday candlestick chart`}>
          <defs>
            <linearGradient id="osChartBg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#151b29" />
              <stop offset="100%" stopColor="#0d1119" />
            </linearGradient>
            <linearGradient id="osAreaFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          <rect x={padL} y={padT} width={plotW} height={plotH} fill="url(#osChartBg)" />
          {tickVals.map((v) => (
            <g key={`yt-${v}`}>
              {prefs.showGrid && <line x1={padL} y1={y(v)} x2={plotRight} y2={y(v)} className="os-chart-grid" />}
              <text x={padL - 10} y={y(v) + 4} className="os-chart-ylabel" textAnchor="end">
                {fmtIdx(v)}
              </text>
            </g>
          ))}
          {xTickIndexes.map((index) => {
            const x = padL + index * barW + barW / 2;
            const showVerticalGrid = !denseMinuteAxis || index % 5 === 0 || index === rows.length - 1;
            return (
              <g key={`xt-${index}`}>
                {prefs.showGrid && showVerticalGrid && <line x1={x} y1={padT} x2={x} y2={h - padB} className="os-chart-grid vertical" />}
                <text
                  x={x}
                  y={denseMinuteAxis ? h - 8 : h - 13}
                  className="os-chart-xlabel"
                  textAnchor={denseMinuteAxis ? 'end' : 'middle'}
                  transform={denseMinuteAxis ? `rotate(-60 ${x} ${h - 8})` : undefined}
                >
                  {chartTimeLabel(rows[index]?.t)}
                </text>
              </g>
            );
          })}
          {nearbyGuides.map((line) => (
            <line
              key={line.key}
              x1={padL}
              y1={y(line.value)}
              x2={plotRight}
              y2={y(line.value)}
              stroke={line.color}
              strokeWidth={line.solid ? 1.8 : 1.2}
              strokeDasharray={line.solid ? undefined : '7 5'}
              opacity="0.9"
            />
          ))}
          {prefs.showVolume && rows.map((row, i) => {
            const volumeHeight = ((Number(row.volume) || 0) / maxVolume) * 42;
            const x = padL + i * barW + barW * 0.19;
            return (
              <rect
                key={`v-${row.t || i}`}
                x={x}
                y={h - padB - volumeHeight}
                width={Math.max(2, barW * 0.62)}
                height={volumeHeight}
                fill={row.bull ? '#22c55e' : '#ef4444'}
                opacity="0.2"
              />
            );
          })}
          {prefs.chartType === 'area' && (
            <polygon points={areaPoints} fill="url(#osAreaFill)" />
          )}
          {(prefs.chartType === 'line' || prefs.chartType === 'area') && (
            <polyline points={linePoints} fill="none" stroke="#60a5fa" strokeWidth="2.2" strokeLinejoin="round" />
          )}
          {renderCandles && rows.map((c, i) => {
            const centerX = padL + i * barW + barW / 2;
            const bw = Math.max(3.5, Math.min(13, barW * 0.62));
            const x = centerX - bw / 2;
            const color = c.bull ? '#22c55e' : '#ef4444';
            const yO = y(c.open);
            const yC = y(c.close);
            return (
              <g key={`c-${c.t || i}`}>
                <title>
                  {`${chartTimeLabel(c.t)} · O ${fmtIdx(c.open)} · H ${fmtIdx(c.high)} · L ${fmtIdx(c.low)} · C ${fmtIdx(c.close)}`}
                </title>
                <line x1={centerX} y1={y(c.high)} x2={centerX} y2={y(c.low)} stroke={color} strokeWidth="1.5" />
                <rect
                  x={x}
                  y={Math.min(yO, yC)}
                  width={bw}
                  height={Math.max(2.5, Math.abs(yO - yC))}
                  fill={color}
                  stroke={color}
                  rx="0.8"
                />
              </g>
            );
          })}
          {indicatorSeries.map((series) => {
            const points = series.values
              .map((value, index) => (Number.isFinite(value)
                ? `${padL + index * barW + barW / 2},${y(value)}`
                : null))
              .filter(Boolean)
              .join(' ');
            return points ? (
              <polyline
                key={series.key}
                points={points}
                fill="none"
                stroke={series.color}
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
            ) : null;
          })}
          {prefs.showLastPrice && spot >= min && spot <= max && (
            <g>
              <line x1={padL} y1={y(spot)} x2={plotRight} y2={y(spot)} className="os-chart-last-line" />
              <rect x={plotRight + 5} y={y(spot) - 11} width="76" height="22" rx="4" className={up ? 'os-chart-last-box up' : 'os-chart-last-box down'} />
              <text x={plotRight + 43} y={y(spot) + 4} className="os-chart-last-text" textAnchor="middle">
                {fmtIdx(spot)}
              </text>
            </g>
          )}
          {prefs.showCrosshair && hovered && (
            <g className="os-chart-crosshair">
              <line
                x1={padL + hoverIndex * barW + barW / 2}
                y1={padT}
                x2={padL + hoverIndex * barW + barW / 2}
                y2={h - padB}
              />
              <line x1={padL} y1={y(hovered.close)} x2={plotRight} y2={y(hovered.close)} />
              <circle cx={padL + hoverIndex * barW + barW / 2} cy={y(hovered.close)} r="4" />
            </g>
          )}
          <rect
            x={padL}
            y={padT}
            width={plotW}
            height={plotH}
            fill="transparent"
            className="os-chart-hover-layer"
            onMouseMove={(event) => {
              if (!prefs.showCrosshair) return;
              const bounds = event.currentTarget.ownerSVGElement.getBoundingClientRect();
              const svgX = ((event.clientX - bounds.left) / bounds.width) * w;
              const index = Math.max(0, Math.min(rows.length - 1, Math.floor((svgX - padL) / barW)));
              setHoverIndex(index);
            }}
            onMouseLeave={() => setHoverIndex(null)}
          />
        </svg>
      </div>

      {(plan || analysis?.reasons?.length) && (
        <div className="os-strategy-map">
          {plan && (
            <p>
              <strong>Trade map:</strong> {plan.strategy} · {chart.planStatus || 'PLAN'}
              {' · '}Entry {fmtIdx(plan.entrySpot)}
              {' → '}Bank {fmtIdx(plan.bankSpot)}
              {' → '}Target {fmtIdx(plan.targetSpot)}
              {' · '}Stop {fmtIdx(plan.stopSpot)}
            </p>
          )}
          {analysis?.reasons?.length ? (
            <p className="os-analysis-reasons">{analysis.reasons.join(' · ')}</p>
          ) : null}
        </div>
      )}
      <p className="os-chain-hidden-note">{footerNote}</p>
    </section>
  );
}

export default function OptionScope({ pollMs = 8000 }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [recheckLeft, setRecheckLeft] = useState(60);
  const [selectedId, setSelectedId] = useState('');
  const [arming, setArming] = useState(false);
  const [armNote, setArmNote] = useState('');

  const refresh = useCallback(async (indexId) => {
    const id = indexId || selectedId || '';
    try {
      const { data: payload } = await axios.get('/api/options/scope', {
        params: id ? { index: id } : undefined,
        timeout: 25000
      });
      if (payload?.ok === false) {
        setError(payload.error || 'OptionScope failed');
        return;
      }
      setData(payload);
      if (payload.selectedIndex) setSelectedId(payload.selectedIndex);
      setError(null);
      const poll = Number(payload?.auto?.pollSeconds || payload?.settings?.pollSeconds) || 60;
      setRecheckLeft(poll);
    } catch (e) {
      setError(e.message || 'Cannot load OptionScope');
    }
  }, [selectedId]);

  useEffect(() => {
    refresh(selectedId);
    const t = setInterval(() => refresh(selectedId), pollMs);
    return () => clearInterval(t);
  }, [selectedId, pollMs, refresh]);

  useEffect(() => {
    const t = setInterval(() => {
      setRecheckLeft((s) => (s <= 1
        ? (Number(data?.auto?.pollSeconds || data?.settings?.pollSeconds) || 60)
        : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [data?.auto?.pollSeconds, data?.settings?.pollSeconds]);

  const selectIndex = async (card) => {
    if (!card?.id) return;
    if (data?.auto?.hasOpenPosition && card.id !== data.auto.openPositionUnderlying) {
      setSelectedId(card.id);
      setArmNote(`View only · open ${data.auto.openPositionUnderlying} trade remains armed; index switching is locked until exit`);
      await refresh(card.id);
      return;
    }
    const alreadySynchronized = card.id === selectedId
      && data?.settings?.underlyingName === card.id
      && Number(data?.settings?.armedLotSize) === Number(card.lotSize);
    if (alreadySynchronized) return;
    setSelectedId(card.id);
    setArmNote('');
    if (!card.tradeable) {
      setArmNote(`${card.label} — chart only (no option auto-buy)`);
      return;
    }
    setArming(true);
    try {
      const lotSize = Number(card.lotSize) > 0
        ? Number(card.lotSize)
        : ({ NIFTY: 65, BANKNIFTY: 15, FINNIFTY: 25, MIDCPNIFTY: 50, SENSEX: 10 }[card.id] || 65);
      await axios.post('/api/automation/settings', {
        underlyingName: card.id,
        symbol: card.yahoo,
        strikeStep: card.strikeStep || 50,
        lots: 1,
        lotSize,
        enabled: true,
        handsFree: true
      }, { timeout: 12000 });
      await axios.post('/api/automation/enable', {}, { timeout: 8000 }).catch(() => null);
      setArmNote(`Intraday armed · ${card.id} · BUY CALL only · 1 lot`);
      await refresh(card.id);
    } catch (err) {
      setArmNote(err.response?.data?.message || err.message || 'Could not arm auto-buy');
    } finally {
      setArming(false);
    }
  };

  const profit = data?.profitBoard || data?.profit;
  const auto = data?.auto;
  const chart = data?.chart;
  const preview = data?.tradePreview;
  const instruction = data?.tradeInstruction;
  const indexList = data?.indexList
    || Object.values(data?.indices || {}).filter((x) => x && x.id);
  const stopRs = auto?.stopLossRs ?? data?.settings?.stopLossPerTrade ?? 0;
  const zeroLoss = stopRs <= 0;

  return (
    <div className="os-app os-kite">
      <div className="os-ticker-strip" role="tablist" aria-label="Indexes">
        {indexList.map((card) => (
          <IndexTicker
            key={card.id}
            card={card}
            active={card.id === selectedId}
            onSelect={selectIndex}
            arming={arming}
          />
        ))}
        {!indexList.length && (
          <div className="os-ticker-loading">Loading indexes…</div>
        )}
      </div>

      <header className="os-header-slim">
        <div className="os-brand-slim">
          <strong>OptionScope · Intraday</strong>
          <span>Signal → exact order → chart → ₹10,000 NET progress</span>
        </div>
        <div className="os-header-status">
          <span className="os-pill live">INTRADAY 1 LOT</span>
          <span className="os-pill zero">{selectedId || '—'} × {Number(preview?.lotSize || auto?.lotSize || 0)}</span>
          <span className={`os-pill ${((profit?.dayNet) || 0) >= 0 ? 'zero' : 'status'}`}>
            DAY {fmtPnl(profit?.dayNet || 0)}
          </span>
          <span className="os-pill zero">{zeroLoss ? '0% LOSS' : `STOP ₹${stopRs}`}</span>
          <span className="os-pill status">{(auto?.status || 'idle').replace(/_/g, ' ')}</span>
        </div>
      </header>

      <div className="os-stage">
        <TradeInstructionBar instruction={instruction} />
        <div className="os-essential-status">
          <strong>{auto?.lastAction || 'Bot market signal का इंतजार कर रहा है'}</strong>
          <span className={auto?.botSynchronized ? 'os-sync-ok' : 'os-sync-warn'}>
            {auto?.hasOpenPosition && auto?.openPositionUnderlying !== selectedId
              ? `VIEW ONLY · open trade ${auto.openPositionUnderlying} पर है`
              : auto?.botSynchronized
                ? `BOT SYNCED · ${auto?.armedUnderlying} · 1 lot × ${auto?.armedLotSize}`
                : `SYNC REQUIRED · ${selectedId} index दोबारा चुनें`}
          </span>
          <span>Next analysis {Math.floor(Math.max(0, recheckLeft) / 60)}:{String(Math.max(0, recheckLeft) % 60).padStart(2, '0')}</span>
          {(armNote || arming) && <span>{arming ? 'Arming…' : armNote}</span>}
        </div>
        <CenterChart chart={chart} />
        <ProfitBoard profit={profit} />
      </div>

      {error && <div className="os-error">{error}</div>}
    </div>
  );
}
